import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "futsal_gent_cookie_consent";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences - don't show banner
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
        setShowBanner(false); // Explicitly keep banner hidden
      } catch (e) {
        // Invalid saved data, clear it and show banner
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        const timer = setTimeout(() => setShowBanner(true), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptNecessary = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-900 border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
          {!showSettings ? (
            // Main Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-600/20 rounded-full shrink-0">
                  <Cookie className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">
                    Wij gebruiken cookies
                  </h3>
                  <p className="text-slate-300 text-sm mb-4">
                    Wij gebruiken cookies om je ervaring op onze website te verbeteren, 
                    het websiteverkeer te analyseren en gepersonaliseerde content aan te bieden. 
                    Je kunt je voorkeuren aanpassen of alle cookies accepteren.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={acceptAll}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Alles accepteren
                    </Button>
                    <Button
                      onClick={acceptNecessary}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                      data-testid="cookie-accept-necessary"
                    >
                      Alleen noodzakelijk
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="ghost"
                      className="text-slate-400 hover:text-white hover:bg-slate-800"
                      data-testid="cookie-settings"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Instellingen
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Lees meer in ons{" "}
                    <Link to="/privacy" className="text-blue-400 hover:underline">
                      privacybeleid
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Cookie-instellingen
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSettings(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Noodzakelijke cookies</h4>
                    <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">
                      Altijd actief
                    </span>
                  </div>
                  <p className="text-sm text-slate-400">
                    Deze cookies zijn essentieel voor de basisfuncties van de website, 
                    zoals paginanavigatie, toegang tot beveiligde gebieden en het winkelmandje.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Analytische cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-analytics-toggle"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-400">
                    Deze cookies helpen ons te begrijpen hoe bezoekers onze website gebruiken 
                    door anonieme informatie te verzamelen en te rapporteren.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Marketing cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                        data-testid="cookie-marketing-toggle"
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-sm text-slate-400">
                    Deze cookies worden gebruikt om advertenties relevanter te maken 
                    en om de effectiviteit van advertentiecampagnes te meten.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={saveCustomPreferences}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="cookie-save-preferences"
                >
                  Voorkeuren opslaan
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Alles accepteren
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook to get cookie preferences
export function useCookieConsent() {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) {
      try {
        setPreferences(JSON.parse(consent));
      } catch (e) {
        // Use defaults
      }
    }
  }, []);

  return preferences;
}
